<template>
  <nav aria-label="Tree" class="ui-tree">
    <TreeSearch
      v-if="!unsearchable"
      v-model="searchStorage"
      v-model:search="clearedSearch"
      :number-found
      :is-loading
      :placeholder
      :size
      :hide-arrows="isSearchInfoVisible"
      :container-ref
    />
    <UIAlert v-if="isSearchInfoVisible" type="warn" class="ui-tree__info">
      Search degraded due to large data size.
      <slot name="info" />
    </UIAlert>
    <div ref="containerRef" class="ui-tree__container">
      <TreeItem
        :key="searchRenderKey"
        :expanded
        :node
        :key-value="root"
        :path="[root]"
        :hidable-names
        :active-path
        is-root
        is-opened-container
        @click:name="handleClickName"
        @click:node="handleClickNode"
        @on:contextmenu="handleOnContextmenu"
        @container:scroll="handleContainerScroll"
        @container:getscroll="handleGetContainerScroll"
      >
        <template #before="slotProps">
          <slot name="before-item" v-bind="slotProps" />
        </template>
        <template #default="slotProps">
          <slot name="meta" v-bind="slotProps" />
        </template>
        <template #name="slotProps">
          <slot name="name" v-bind="slotProps" />
        </template>
        <template v-if="$slots.actions" #actions="slotProps">
          <slot name="actions" v-bind="slotProps" />
        </template>
      </TreeItem>
      <slot name="after" />
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick, provide, ref, watch, useTemplateRef, onUnmounted } from 'vue'
import { get, has, pick, set } from 'lodash'
import { debouncedRef, watchPausable } from '@vueuse/core'

import {
  Tree,
  TreeNodeDescriptionGetter,
  TreeNodeFormatter,
} from './utils/types'
import { InputSize } from '@types'

import {
  DEBOUNCE_DELAY,
  DEGRADED_SEARCH_THRESHOLD,
  HASH_DELIMITER,
  TREE_ITEM_FOUND_CLASS,
  TREE_LAZY_CLASS,
} from './utils/const'

import { waitForNode } from '@/helpers/common'
import { generateHashData, scrollToNode } from './utils/helpers'

import TreeItem from './components/TreeItem.vue'
import TreeSearch from './components/TreeSearch.vue'
import UIAlert from '../Alert/Alert.vue'

type Props = {
  data?: Tree
  root: string

  metaFields?: string[]
  wide?: boolean
  expanded?: boolean
  searchInMeta?: boolean
  hidableNames?: boolean

  placeholder?: string
  size?: InputSize

  formatter?: TreeNodeFormatter
  getDescription?: TreeNodeDescriptionGetter

  sortField?: string
  sortDir?: 'asc' | 'desc'

  activePath?: string[]

  unsearchable?: boolean
}

type Emits = {
  'click:name': [item: Tree, isLeaf: boolean]
  'click:node': [path: string[], value: any]
  'on:contextmenu': [item: Tree, isLeaf: boolean]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const searchStorage = defineModel<string>('search', { default: '' })

defineOptions({
  name: 'UITree',
})

const containerRef = useTemplateRef('containerRef')

const clearedSearch = ref(searchStorage.value)
const debouncedSearch = debouncedRef(clearedSearch, DEBOUNCE_DELAY)
const isInitialLoadDone = ref(false)
const isSearchInfoVisible = ref(false)

const effectiveSearch = computed(() =>
  isInitialLoadDone.value ? debouncedSearch.value : searchStorage.value,
)

provide('metaFields', props.metaFields)
provide('search', effectiveSearch)
provide('wide', props.wide)
provide('formatter', props.formatter)
provide('getDescription', props.getDescription)
provide('sortField', props.sortField)
provide('sortDir', props.sortDir || 'desc')

const isLoading = ref(false)
const searchRenderKey = ref(0)

const loaderTimeout = ref<NodeJS.Timeout>()

const highlightedNodeIndex = ref(0)

provide('highlightedNodeIndex', highlightedNodeIndex)

const hashData = computed(() =>
  props.unsearchable ? {} : generateHashData(
    props.data,
    props.metaFields,
    props.formatter,
    props.getDescription,
    true,
  ),
)
const foundData = computed(() =>
  effectiveSearch.value
    ? Object.entries(hashData.value).filter(([key, { value }]) => {
      if (key === props.root) return
      const search = effectiveSearch.value.toLowerCase()
      const keys = key.split(HASH_DELIMITER)
      return (
        keys.at(-1)?.includes(search) ||
            (props.searchInMeta && `${value}`.toLowerCase().includes(search))
      )
    })
    : [],
)
const searchResult = computed(() => foundData.value.map(([key]) => key))
const numberFound = computed(() => searchResult.value.length)
provide('searchResult', searchResult)

const node = computed(() => {
  if (!foundData.value.length) return get(props.data, [props.root])
  const result = {}
  foundData.value.forEach(([, { path }]) => {
    const computedPath: string[] = []
    path.forEach(item => {
      computedPath.push(item)
      if (has(result, computedPath)) return
      const node = get(props.data, computedPath)
      if (!node) return
      set(result, computedPath, pick(node, props.metaFields || []))
    })
  })
  return get(result, [props.root])
})

const handleOnContextmenu = (item: Tree, isLeaf: boolean) => {
  emit('on:contextmenu', item, isLeaf)
}

const handleClickName = (item: Tree, isLeaf: boolean) => {
  emit('click:name', item, isLeaf)
}

const handleClickNode = (path: string[], value: any) => {
  emit('click:node', path, value)
}

const handleGetContainerScroll = (callback: (top: number) => void) => {
  callback(containerRef.value?.scrollTop || 0)
}

const handleContainerScroll = (top: number) => {
  containerRef.value?.scrollTo({ top })
}

watch(searchStorage, () => {
  loaderTimeout.value && clearTimeout(loaderTimeout.value)
  isLoading.value = true
  pause()
  nextTick(resume)
})

watch(effectiveSearch, async () => {
  searchRenderKey.value++
  await nextTick()
  isSearchInfoVisible.value = numberFound.value > DEGRADED_SEARCH_THRESHOLD && !!containerRef.value?.querySelector(`.${TREE_LAZY_CLASS}`)
  loaderTimeout.value = setTimeout(() => {
    isLoading.value = false
  }, DEBOUNCE_DELAY)
  if (!highlightedNodeIndex.value) return
  highlightedNodeIndex.value = 0
})

const { pause, resume } = watchPausable(numberFound, async value => {
  loaderTimeout.value && clearTimeout(loaderTimeout.value)
  isLoading.value = false
  if (!value) return
  await waitForNode(`.${TREE_ITEM_FOUND_CLASS}`)
  highlightedNodeIndex.value = 1
  scrollToNode(containerRef.value, highlightedNodeIndex.value)
}, { immediate: true })

let initialLoadTimeout: ReturnType<typeof setTimeout> | undefined
onMounted(() => {
  initialLoadTimeout = setTimeout(() => {
    isInitialLoadDone.value = true
  }, DEBOUNCE_DELAY)
})
onUnmounted(() => {
  if (initialLoadTimeout !== undefined) {
    clearTimeout(initialLoadTimeout)
    initialLoadTimeout = undefined
  }
})
</script>

<style>
.ui-tree {
  @apply flex flex-col flex-auto;
  @apply max-w-full;

  &__info {
    @apply p-2 mb-2;
    @apply rounded;

    .ui-alert__message {
      @apply text-sm sm:text-xs;
      @apply font-normal;
    }
  }

  &__container {
    @apply flex-auto;
    @apply md:h-0;
    @apply overflow-y-auto;
    @apply overflow-x-hidden;
  }
}
</style>
